import React from 'react';
import { HStack, Icon, Spinner, Text, VStack } from '@chakra-ui/react';
import { IconButton } from '@/components/ui/icon-button';
import { LuChevronRight, LuMessageCircle } from 'react-icons/lu';
import { ExchangeOut, SessionOut } from '@/client';
import { useAssistantExchanges } from '@/hooks/assistant/useAssistantExchanges';
import { useAuth } from '@/context/AuthContext';
import { getUserDisplayName } from '@/utils/userDisplayUtils';

export interface SessionRowProps {
	session: SessionOut;
	houseId: number;
	onResume: (sessionId: string, exchanges: ExchangeOut[]) => void;
	onSelectCurrent?: () => void;
	isActive?: boolean;
	optimisticTitle?: string;
	isStreaming?: boolean;
}

const getSessionTitle = (chronologicalExchanges: ExchangeOut[] | undefined, optimisticTitle?: string): string => {
	for (const exchange of chronologicalExchanges ?? []) {
		const firstPrompt = exchange.messages.find((message) => message.role === 'user')?.text.trim();
		if (firstPrompt) return firstPrompt;
	}
	return optimisticTitle?.trim() || 'New chat';
};

/**
 * Single session row — fetches its own exchanges to surface the first user question as the title.
 * The resume IconButton is hidden by default and revealed on hover to keep the list clean.
 */
const SessionRow: React.FC<SessionRowProps> = ({
	session,
	houseId,
	onResume,
	onSelectCurrent,
	isActive = false,
	optimisticTitle,
	isStreaming = false,
}) => {
	// TODO(chat-history-n-plus-one): This issues one exchanges request per session row so the list can
	// derive a title from the first user message. Add a title field to SessionOut
	// (or a bulk session-summary endpoint), then fetch exchanges only when the user resumes a session.
	const { data: exchanges, isLoading } = useAssistantExchanges(houseId, isStreaming ? null : session.session_id);
	const { currentUser } = useAuth();

	// The API returns newest-first; normalize once for both the stable title and resume payload.
	const chronologicalExchanges = exchanges ? [...exchanges].reverse() : [];
	const sessionTitle = getSessionTitle(chronologicalExchanges, optimisticTitle);
	// TODO: Replace this current-user fallback with session creator metadata when the sessions API exposes it.
	const userLabel = getUserDisplayName(currentUser) || currentUser?.email || null;

	const handleResume = () => {
		if (isStreaming) {
			onSelectCurrent?.();
			return;
		}
		onResume(session.session_id, chronologicalExchanges);
	};

	return (
		<HStack
			px={4}
			py={3}
			borderBottom="1px solid"
			borderColor="border.default"
			gap={3}
			align="center"
			cursor="pointer"
			bg={isActive ? 'surface.subtle' : 'transparent'}
			transition="background 0.15s"
			_hover={
				isStreaming ? { bg: 'surface.subtle' } : { bg: 'surface.subtle', '& [data-resume-btn]': { opacity: 1 } }
			}
			onClick={handleResume}
		>
			{isStreaming ? (
				<Spinner boxSize={4} color="brand.fg" flexShrink={0} role="status" aria-label="Answering" />
			) : (
				<Icon as={LuMessageCircle} boxSize={4} color={isActive ? 'fg.muted' : 'fg.subtle'} flexShrink={0} />
			)}

			{/* Text content */}
			<VStack align="start" gap={0} flex={1} minW={0}>
				{isLoading && !optimisticTitle ? (
					<HStack gap={2}>
						<Spinner size="xs" />
						<Text fontSize="sm" color="fg.muted">
							Loading…
						</Text>
					</HStack>
				) : (
					<Text textStyle="label" lineClamp={1} title={sessionTitle}>
						{sessionTitle}
					</Text>
				)}
				<HStack gap={2} minW={0} w="full">
					<Text fontSize="xs" color="fg.muted" flexShrink={0}>
						{new Date(session.created_at).toLocaleDateString(undefined, {
							month: 'short',
							day: 'numeric',
							year: 'numeric',
						})}
					</Text>
					<Text fontSize="xs" color="fg.muted" display="none" flexShrink={0}>
						·
					</Text>
					{userLabel && (
						<>
							<Text fontSize="xs" color="fg.muted" flexShrink={0}>
								·
							</Text>
							<Text fontSize="xs" color="fg.muted" minW={0} truncate>
								{userLabel}
							</Text>
						</>
					)}
					<Text fontSize="xs" color="fg.muted" flexShrink={0}>
						·
					</Text>
					<Text fontSize="xs" color="fg.muted" flexShrink={0}>
						{session.exchange_count} {session.exchange_count === 1 ? 'message' : 'messages'}
					</Text>
				</HStack>
			</VStack>

			{/* Resume icon button — hidden until row is hovered */}
			{!isStreaming && (
				<IconButton
					data-resume-btn
					aria-label="Resume conversation"
					size="sm"
					variant="ghost"
					color="brand.fg"
					opacity={isActive ? 1 : 0}
					transition="opacity 0.15s"
					onClick={(e) => {
						e.stopPropagation();
						handleResume();
					}}
				>
					<LuChevronRight />
				</IconButton>
			)}
		</HStack>
	);
};

export default SessionRow;
